import Cookies from "js-cookie";
import type { AddressData } from "@/types/address";

const COOKIE_KEYS = {
	DASHBOARD_ADDRESS: "dashboard_address",
	DASHBOARD_COUNTY_STATE: "dashboard_county_state",
	DASHBOARD_DATE_FILTERS: "dashboard_date_filters",
	DASHBOARD_NUMERICAL_FILTERS: "dashboard_numerical_filters",
	LOCATION_PERMISSION: "location_permission",
	SELECTED_AUCTION_DATE: "selected_auction_date",
	DASHBOARD_VIEW: "dashboard_view",
	CHECKOUT_INTENT: "checkout_intent",
	IMAGERY_EMAIL: "imagery_email",
} as const;

export type LocationPermissionStatus =
	| "allowed"
	| "denied"
	| "ask_next_time"
	| null;

const COOKIE_OPTIONS = {
	expires: 30, // 30 days
	sameSite: "lax" as const,
	secure: process.env.NODE_ENV === "production",
};

/**
 * Save address data to cookies
 */
export const saveAddressToCookie = (address: AddressData | null) => {
	if (address) {
		Cookies.set(
			COOKIE_KEYS.DASHBOARD_ADDRESS,
			JSON.stringify(address),
			COOKIE_OPTIONS
		);
	} else {
		Cookies.remove(COOKIE_KEYS.DASHBOARD_ADDRESS);
	}
};

/**
 * Load address data from cookies
 */
export const loadAddressFromCookie = (): AddressData | null => {
	const addressStr = Cookies.get(COOKIE_KEYS.DASHBOARD_ADDRESS);
	if (addressStr) {
		try {
			return JSON.parse(addressStr) as AddressData;
		} catch (error) {
			console.error("Failed to parse address from cookie:", error);
			return null;
		}
	}
	return null;
};

/**
 * Save county/state filter to cookies
 */
export const saveCountyStateToCookie = (countyState: string) => {
	if (countyState) {
		Cookies.set(
			COOKIE_KEYS.DASHBOARD_COUNTY_STATE,
			countyState,
			COOKIE_OPTIONS
		);
	} else {
		Cookies.remove(COOKIE_KEYS.DASHBOARD_COUNTY_STATE);
	}
};

/**
 * Load county/state filter from cookies
 */
export const loadCountyStateFromCookie = (): string => {
	return Cookies.get(COOKIE_KEYS.DASHBOARD_COUNTY_STATE) || "";
};

/**
 * Save date filters to cookies
 */
export const saveDateFiltersToCookie = (
	dateFilters: Record<string, { operator?: string; value?: string }>
) => {
	if (Object.keys(dateFilters).length > 0) {
		Cookies.set(
			COOKIE_KEYS.DASHBOARD_DATE_FILTERS,
			JSON.stringify(dateFilters),
			COOKIE_OPTIONS
		);
	} else {
		Cookies.remove(COOKIE_KEYS.DASHBOARD_DATE_FILTERS);
	}
};

/**
 * Load date filters from cookies
 */
export const loadDateFiltersFromCookie = (): Record<
	string,
	{ operator?: string; value?: string }
> => {
	const filtersStr = Cookies.get(COOKIE_KEYS.DASHBOARD_DATE_FILTERS);
	if (filtersStr) {
		try {
			return JSON.parse(filtersStr) as Record<
				string,
				{ operator?: string; value?: string }
			>;
		} catch (error) {
			console.error("Failed to parse date filters from cookie:", error);
			return {};
		}
	}
	return {};
};

/**
 * Save numerical filters to cookies
 */
export const saveNumericalFiltersToCookie = (
	numericalFilters: Record<string, { operator?: string; value?: number }>
) => {
	if (Object.keys(numericalFilters).length > 0) {
		Cookies.set(
			COOKIE_KEYS.DASHBOARD_NUMERICAL_FILTERS,
			JSON.stringify(numericalFilters),
			COOKIE_OPTIONS
		);
	} else {
		Cookies.remove(COOKIE_KEYS.DASHBOARD_NUMERICAL_FILTERS);
	}
};

/**
 * Load numerical filters from cookies
 */
export const loadNumericalFiltersFromCookie = (): Record<
	string,
	{ operator?: string; value?: number }
> => {
	const filtersStr = Cookies.get(COOKIE_KEYS.DASHBOARD_NUMERICAL_FILTERS);
	if (filtersStr) {
		try {
			return JSON.parse(filtersStr) as Record<
				string,
				{ operator?: string; value?: number }
			>;
		} catch (error) {
			console.error(
				"Failed to parse numerical filters from cookie:",
				error
			);
			return {};
		}
	}
	return {};
};

/**
 * Save location permission decision to cookies
 */
export const saveLocationPermission = (status: LocationPermissionStatus) => {
	if (status) {
		Cookies.set(COOKIE_KEYS.LOCATION_PERMISSION, status, COOKIE_OPTIONS);
	} else {
		Cookies.remove(COOKIE_KEYS.LOCATION_PERMISSION);
	}
};

/**
 * Load location permission decision from cookies
 */
export const loadLocationPermission = (): LocationPermissionStatus => {
	const permission = Cookies.get(COOKIE_KEYS.LOCATION_PERMISSION);
	if (
		permission === "allowed" ||
		permission === "denied" ||
		permission === "ask_next_time"
	) {
		return permission;
	}
	return null;
};

/**
 * Save selected auction date to cookies
 */
export const saveSelectedAuctionDate = (date: string | null) => {
	if (date) {
		Cookies.set(COOKIE_KEYS.SELECTED_AUCTION_DATE, date, COOKIE_OPTIONS);
	} else {
		Cookies.remove(COOKIE_KEYS.SELECTED_AUCTION_DATE);
	}
};

/**
 * Load selected auction date from cookies
 */
export const loadSelectedAuctionDate = (): string | null => {
	return Cookies.get(COOKIE_KEYS.SELECTED_AUCTION_DATE) || null;
};

/**
 * Save dashboard view preference to cookies
 */
export const saveDashboardView = (view: "table" | "map" | "auction") => {
	Cookies.set(COOKIE_KEYS.DASHBOARD_VIEW, view, COOKIE_OPTIONS);
};

/**
 * Load dashboard view preference from cookies
 */
export const loadDashboardView = (): "table" | "map" | "auction" | null => {
	const view = Cookies.get(COOKIE_KEYS.DASHBOARD_VIEW);
	if (view === "table" || view === "map" || view === "auction") {
		return view;
	}
	return null;
};

/**
 * Save checkout intent to cookies
 * Used to persist checkout intent across OAuth redirects
 */
export interface CheckoutIntent {
	priceId: string;
	successUrl: string;
	cancelUrl: string;
	returnUrl: string;
}

export const saveCheckoutIntent = (intent: CheckoutIntent | null) => {
	if (intent) {
		// Use shorter expiration for checkout intent (1 hour)
		// This is a temporary intent that should expire quickly
		Cookies.set(COOKIE_KEYS.CHECKOUT_INTENT, JSON.stringify(intent), {
			expires: 1 / 24, // 1 hour
			sameSite: "lax" as const,
			secure: process.env.NODE_ENV === "production",
		});
	} else {
		Cookies.remove(COOKIE_KEYS.CHECKOUT_INTENT);
	}
};

/**
 * Load checkout intent from cookies
 */
export const loadCheckoutIntent = (): CheckoutIntent | null => {
	const intentStr = Cookies.get(COOKIE_KEYS.CHECKOUT_INTENT);
	if (intentStr) {
		try {
			return JSON.parse(intentStr) as CheckoutIntent;
		} catch (error) {
			console.error(
				"Failed to parse checkout intent from cookie:",
				error
			);
			return null;
		}
	}
	return null;
};

/**
 * Clear all dashboard filter cookies
 */
export const clearDashboardCookies = () => {
	Cookies.remove(COOKIE_KEYS.DASHBOARD_ADDRESS);
	Cookies.remove(COOKIE_KEYS.DASHBOARD_COUNTY_STATE);
	Cookies.remove(COOKIE_KEYS.DASHBOARD_DATE_FILTERS);
	Cookies.remove(COOKIE_KEYS.DASHBOARD_NUMERICAL_FILTERS);
};

export interface ImagerySessionCookie {
	email: string;
	enrichmentPublicEntityId: string | null;
}

/** Persist email + public entity id (from POST /imagery/check-in) per integration doc */
export const setImagerySessionCookie = (
	email: string,
	enrichmentPublicEntityId: string
) => {
	if (!email?.includes("@")) return;
	Cookies.set(
		COOKIE_KEYS.IMAGERY_EMAIL,
		JSON.stringify({ email, enrichmentPublicEntityId }),
		COOKIE_OPTIONS
	);
};

export const getImagerySessionCookie = (): ImagerySessionCookie | null => {
	const raw = Cookies.get(COOKIE_KEYS.IMAGERY_EMAIL);
	if (!raw) return null;
	try {
		const j = JSON.parse(raw) as {
			email?: string;
			enrichmentPublicEntityId?: string | null;
		};
		if (j?.email && j.email.includes("@")) {
			return {
				email: j.email,
				enrichmentPublicEntityId:
					typeof j.enrichmentPublicEntityId === "string"
						? j.enrichmentPublicEntityId
						: null,
			};
		}
	} catch {
		if (raw.includes("@")) {
			return { email: raw, enrichmentPublicEntityId: null };
		}
	}
	return null;
};

/** @deprecated use setImagerySessionCookie after check-in */
export const setImageryEmailCookie = (email: string | null) => {
	if (email && email.includes("@")) {
		Cookies.set(COOKIE_KEYS.IMAGERY_EMAIL, email, COOKIE_OPTIONS);
	} else {
		Cookies.remove(COOKIE_KEYS.IMAGERY_EMAIL);
	}
};

export const getImageryEmailCookie = (): string | null => {
	return getImagerySessionCookie()?.email ?? null;
};

export const clearImageryEmailCookie = () => {
	Cookies.remove(COOKIE_KEYS.IMAGERY_EMAIL);
};
